import { MapPin, PencilIcon, PlusIcon, TrashIcon } from 'lucide-react'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { PageHeader } from '@/components/ds'
import { EmptyState } from '@/components/EmptyState'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { PickupPointForm } from '@/forms/PickupPointForm/PickupPointForm'
import { Collection } from '@/interfaces/Collection'
import { PickupPoint } from '@/interfaces/eshop'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'

import { EnabledBadge, SkeletonRows } from './components/EshopParts'

const stop = (e: MouseEvent) => e.stopPropagation()

export default function EshopPickupPointsPage() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop.pickupPoints',
  })
  const { t: te } = useTranslation(undefined, {
    keyPrefix: 'eshop',
  })
  const { t: td } = useTranslation(undefined, {
    keyPrefix: 'eshop.deliveryMethods',
  })

  const {
    data: pickupPoints,
    isLoading,
    mutate,
  } = useSWR<Collection<PickupPoint>>('/api/v2/pickup-points', apiFetcher)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingPoint, setEditingPoint] = useState<PickupPoint | null>(null)
  const [deletingPoint, setDeletingPoint] = useState<PickupPoint | null>(null)

  const openCreateDialog = () => {
    setEditingPoint(null)
    setDialogOpen(true)
  }

  const openEditDialog = (point: PickupPoint) => {
    setEditingPoint(point)
    setDialogOpen(true)
  }

  const onDelete = async () => {
    if (!deletingPoint) return
    try {
      await API.delete(`/api/v2/pickup-points/${deletingPoint.id}`)
      toast.success(t('delete.success'))
      mutate()
    } catch (error: unknown) {
      toast.error(t('delete.error'), {
        description: extractErrorMessage(error),
      })
    }
    setDeletingPoint(null)
  }

  useSetPageHeader({
    breadcrumbs: [
      { href: '/eshop/delivery-methods', children: te('title') },
      { href: '/eshop/delivery-methods', children: td('title') },
      { href: '/eshop/pickup-points', children: t('title') },
    ],
  })

  const points = pickupPoints?.data ?? []

  return (
    <>
      <title>{te('title') + ', ' + t('title')}</title>

      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader
          title={t('title')}
          description={t('description')}
          actions={
            <Button onClick={openCreateDialog}>
              <PlusIcon />
              {t('actions.create')}
            </Button>
          }
        />

        {!isLoading && pickupPoints && points.length === 0 ? (
          <EmptyState icon={MapPin}
            title={t('empty')}
            description="Odběrné místo uvidí zákazník v e-shopu u dopravy Osobní odběr."
            ctaButton={
              <Button onClick={openCreateDialog}>
                <PlusIcon />
                {t('actions.create')}
              </Button>
            }
          />
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('fields.name')}</TableHead>
                  <TableHead className="hidden md:table-cell">Adresa</TableHead>
                  <TableHead className="hidden md:table-cell">{t('fields.openingHours')}</TableHead>
                  <TableHead className="hidden w-32 md:table-cell">Stav</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <SkeletonRows rows={3} cols={5} mobileCols={[0, 4]} />}
                {points.map((point) => (
                  <TableRow key={point.id} onClick={() => openEditDialog(point)} className="group cursor-pointer">
                    <TableCell className="w-full max-w-0 md:w-auto">
                      <p className="truncate font-medium">{point.name}</p>
                      {point.note && <p className="hidden truncate text-xs text-muted-foreground md:block">{point.note}</p>}
                      <p className="truncate text-xs text-muted-foreground md:hidden">
                        {!point.isActive && 'Vypnuto, '}
                        {[point.street, point.city].filter(Boolean).join(', ')}
                      </p>
                    </TableCell>
                    <TableCell className="hidden max-w-0 md:table-cell">
                      <p className="truncate">{point.street}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {point.zip} {point.city}, {point.country}
                      </p>
                    </TableCell>
                    <TableCell className="hidden text-muted-foreground md:table-cell">{point.openingHours || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <EnabledBadge enabled={point.isActive} />
                    </TableCell>
                    <TableCell onClick={stop}>
                      <div className="flex items-center justify-end gap-0.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100">
                        <Button variant="ghost" size="icon-sm" aria-label={t('edit.title')} onClick={() => openEditDialog(point)}>
                          <PencilIcon />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('delete.title')}
                          className="hover:text-danger"
                          onClick={() => setDeletingPoint(point)}
                        >
                          <TrashIcon />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <PickupPointForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingPoint={editingPoint}
        onSuccess={() => mutate()}
      />

      <AlertDialog
        open={!!deletingPoint}
        onOpenChange={(open) => !open && setDeletingPoint(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('delete.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{te('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>
              {t('delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
